import React, { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { IconButton } from '@atlaskit/button/new';
import { FlagsProvider } from '@atlaskit/flag';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock-filled';
import UnlockIcon from '@atlaskit/icon/core/migration/lock-unlocked--unlock';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { R400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { ContextualAnalyticsData, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { ActionSubject } from '@atlassian/proforma-utils/analytics';
import { ProformaIntlProvider } from '@atlassian/proforma-utils/intl-provider';
import { FormRestrictionStatus } from '../../common/types';
import { FormPermissionsContainer } from '../../controllers/form-permissions';
import lazyMessages from '../../i18n/lazy';
import { ConfigModal, ViewConfigModal } from './config-modal';
import { messages } from './messages';
import { PulseSpotlightOnboarding } from './onboarding';
/**
 * When using this component, ensure:
 *  * it is wrapped by an `IntlProvider` that provides the locale.
 *  * it is wrapped by an `AnalyticsProvider` to emit analytics.
 */
export const FormSecurityConfigButton = props => {
  const {
    locale
  } = useIntl();
  const {
    loadRestrictedPrincipals,
    saveRestrictedPrincipals
  } = props;
  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    fallbackRender: () => null
  }, /*#__PURE__*/React.createElement(ProformaIntlProvider, {
    messages: lazyMessages,
    locale: locale
  }, /*#__PURE__*/React.createElement(FlagsProvider, null, /*#__PURE__*/React.createElement(FormPermissionsContainer, {
    loadRestrictedPrincipals: loadRestrictedPrincipals,
    saveRestrictedPrincipals: saveRestrictedPrincipals
  }, /*#__PURE__*/React.createElement(ConfigButton, props)))));
};
const ConfigButton = ({
  restrictionStatus,
  appearance,
  spacing,
  isViewMode,
  getWarningMessage,
  viewModeManageRestrictionUrl,
  showOnboarding,
  acknowledgeOnboarding,
  getWarningMessageConfig
}) => {
  const {
    formatMessage
  } = useIntl();
  const isRestricted = restrictionStatus === FormRestrictionStatus.RESTRICTED;
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const openConfigModal = (event, analyticsEvent) => {
    event.stopPropagation();
    fireUIAnalytics(analyticsEvent, ActionSubject.FORM_PERMISSIONS);
    setIsConfigModalOpen(true);
  };
  const getTooltipMessage = () => {
    if (!isRestricted) {
      return messages.openTooltip;
    }
    return isViewMode ? messages.restrictedViewModeTooltip : messages.restrictedTooltip;
  };
  const labelForEditMode = formatMessage(isRestricted ? messages.restrictedLabel : messages.openLabel);
  const labelForViewMode = formatMessage(messages.viewModeButtonLabel);
  const buttonLabel = isViewMode ? labelForViewMode : labelForEditMode;
  const onProceedOnboarding = (event, analyticsEvent) => {
    if (acknowledgeOnboarding) {
      acknowledgeOnboarding();
    }
    openConfigModal(event, analyticsEvent);
  };
  const onDismissOnboarding = () => {
    if (acknowledgeOnboarding) {
      acknowledgeOnboarding();
    }
  };
  return /*#__PURE__*/React.createElement(ContextualAnalyticsData, {
    attributes: {
      restrictionStatus
    }
  }, /*#__PURE__*/React.createElement(Box, {
    onClick: e => e.stopPropagation()
  }, isViewMode ? /*#__PURE__*/React.createElement(ViewConfigModal, {
    isOpen: isConfigModalOpen,
    onClose: () => setIsConfigModalOpen(false),
    getWarningMessage: fg('proforma_granular_permissions_analytic_attributes') ? undefined : getWarningMessage,
    viewModeManageRestrictionUrl: viewModeManageRestrictionUrl
  }) : /*#__PURE__*/React.createElement(ConfigModal, {
    isOpen: isConfigModalOpen,
    onClose: () => setIsConfigModalOpen(false),
    getWarningMessage: fg('proforma_granular_permissions_analytic_attributes') ? undefined : getWarningMessage,
    getWarningMessageConfig: getWarningMessageConfig
  })), fg('granular_permissions_on_forms_ga') ? /*#__PURE__*/React.createElement(Inline, null, /*#__PURE__*/React.createElement(Tooltip, {
    content: formatMessage(getTooltipMessage())
  }, /*#__PURE__*/React.createElement(PulseSpotlightOnboarding, {
    showOnboarding: !!showOnboarding,
    onDismiss: onDismissOnboarding,
    onProceed: onProceedOnboarding
  }, onClickWithOnboarding => /*#__PURE__*/React.createElement(IconButton, {
    appearance: appearance,
    spacing: spacing,
    onClick: onClickWithOnboarding(openConfigModal),
    testId: "proforma-form-security.ui.config-button",
    icon: isRestricted ? RedLockFilledIcon : UnlockIcon,
    label: buttonLabel
  })))) : /*#__PURE__*/React.createElement(Tooltip, {
    content: formatMessage(getTooltipMessage())
  }, /*#__PURE__*/React.createElement(IconButton, {
    appearance: appearance,
    spacing: spacing,
    onClick: openConfigModal,
    testId: "proforma-form-security.ui.config-button",
    icon: isRestricted ? RedLockFilledIcon : UnlockIcon,
    label: formatMessage(isRestricted ? messages.restricted : messages.open)
  })));
};
const RedLockFilledIcon = props => {
  const {
    label
  } = props;
  return /*#__PURE__*/React.createElement(LockLockedIcon, {
    color: `var(--ds-icon-accent-red, ${R400})`,
    LEGACY_size: "medium",
    label: label
  });
};